import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { useGetUserByIdQuery } from '@consolidocs-ui/data-access/UserApi';

/**
 * Applies the language saved on the user's profile once it loads.
 *
 * Resolution priority: profile preference → localStorage → navigator. The
 * latter two are already handled by the i18next language detector at init;
 * this hook only overrides them when the profile carries a valid preference.
 * It runs once per session so a later profile refetch doesn't fight a
 * language the user just picked locally.
 */
const useUserLanguagePreference = (
  supportedLanguages: readonly string[]
) => {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const hasAppliedRef = useRef(false);

  const userId = auth.user?.profile?.sub as string | undefined;
  const { data: userData } = useGetUserByIdQuery(userId || '', {
    skip: !userId,
  });

  useEffect(() => {
    if (hasAppliedRef.current) return;

    const preference = userData?.data?.user_language_preference
      ?.toLowerCase()
      .split('-')[0];
    if (!preference) return;
    if (!supportedLanguages.includes(preference)) {
      return;
    }

    hasAppliedRef.current = true;
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || '')
      .split('-')[0];
    if (preference !== currentLanguage) {
      // Also refreshes the detector's localStorage cache, keeping this
      // device in sync with a preference saved from another one.
      i18n.changeLanguage(preference);
    }
  }, [userData, i18n]);
};

export default useUserLanguagePreference;
